/**
 * usePlateLookup - Consulta de veículo por placa e de multas (multi-provider)
 *
 * Todo acesso ao provedor externo passa pela Edge Function `plate-proxy`,
 * que resolve o tenant, valida as flags do módulo e guarda o token da API.
 * O frontend nunca conhece o provedor nem a chave.
 *
 * @version 2.0.0
 * RELEASE: 2026-08-08 v2.0.0 - Provedor API Placas: retorno passa a incluir os
 *          dados FIPE (código, valor, modelo e marca) quando disponíveis.
 * RELEASE: 2026-08-08 v1.0.0 - Criação inicial (ações lookup e fines)
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';
import { toast } from 'sonner';
import type { VehicleType } from '@/hooks/useCustomerVehicles';

const logger = createLogger('usePlateLookup');

/** Dados normalizados retornados pela consulta por placa */
export interface PlateVehicleData {
  plate: string;
  brand: string | null;
  model: string | null;
  brand_model_raw: string | null;
  year: number | null;
  year_model: number | null;
  fuel: string | null;
  color: string | null;
  chassis: string | null;
  renavam: string | null;
  vehicle_type: VehicleType;
  city: string | null;
  uf: string | null;
  situation: string | null;
  /** v2.0.0 — Dados FIPE devolvidos pelo provedor (podem não vir) */
  fipe_code?: string | null;
  fipe_value?: number | null;
  fipe_model?: string | null;
  fipe_brand?: string | null;
  /** v2.1.0 — IDs resolvidos na base global FIPE (unificação dos provedores) */
  fipe_match?: PlateFipeMatch | null;
  /** v2.2.0 — Todas as correspondências FIPE devolvidas pelo provedor */
  fipe_candidates?: PlateFipeCandidate[];
}

/**
 * Correspondência FIPE candidata (texto puro do provedor de placa).
 * Quando há mais de uma, o formulário abre um modal para o usuário confirmar.
 */
export interface PlateFipeCandidate {
  fipe_code: string | null;
  brand_text: string | null;
  model_text: string | null;
  year: number | null;
  fuel: string | null;
  value: number | null;
  score: number | null;
}


/**
 * Resultado da resolução na base global FIPE, encadeada pela Edge Function
 * `plate-proxy` logo após a consulta por placa. Permite preencher os selects
 * de Marca/Modelo/Ano automaticamente.
 */
export interface PlateFipeMatch {
  matched: boolean;
  /** `confirmed` quando o código FIPE da placa bate com o resolvido */
  confidence?: 'confirmed' | 'suggested';
  reason?: string;
  brand_id?: string | null;
  brand_name?: string | null;
  model_id?: string | null;
  model_name?: string | null;
  year_code?: string | null;
  year_label?: string | null;
  fipe_code?: string | null;
  valor?: number | null;
  mes_referencia?: string | null;
}


/** Multa normalizada */
export interface VehicleFine {
  id?: string;
  auto_infracao: string | null;
  descricao: string | null;
  orgao: string | null;
  local_infracao: string | null;
  data_infracao: string | null;
  vencimento: string | null;
  valor: number | null;
  valor_com_desconto: number | null;
  pontos: number | null;
  status: string;
  synced_at?: string | null;
}

/** Normaliza a placa digitada (A-Z e 0-9, maiúsculas) */
export function normalizePlateInput(value: string): string {
  return (value || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
}

/** Valida placa padrão antigo (ABC1234) ou Mercosul (ABC1D23) */
export function isValidPlate(value: string): boolean {
  return /^[A-Z]{3}[0-9][A-Z0-9][0-9]{2}$/.test(normalizePlateInput(value));
}

/** Extrai a mensagem real de erro devolvida pela Edge Function */
async function readFunctionError(error: unknown): Promise<string> {
  const anyError = error as { context?: { text?: () => Promise<string> }; message?: string };
  try {
    if (anyError?.context?.text) {
      const text = await anyError.context.text();
      const parsed = JSON.parse(text);
      if (parsed?.error) return String(parsed.error);
      return text;
    }
  } catch {
    /* segue com a mensagem padrão */
  }
  return anyError?.message || 'Falha na consulta';
}

/**
 * Consulta de dados do veículo pela placa.
 */
export function usePlateLookup() {
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id ?? null;

  return useMutation({
    mutationFn: async (plate: string): Promise<PlateVehicleData> => {
      const normalized = normalizePlateInput(plate);
      if (!isValidPlate(normalized)) {
        throw new Error('Placa inválida. Use o formato ABC1234 ou ABC1D23.');
      }

      const { data, error } = await supabase.functions.invoke('plate-proxy', {
        body: { action: 'lookup', plate: normalized, tenant_id: tenantId },
      });

      if (error) {
        const message = await readFunctionError(error);
        logger.error('Falha na consulta por placa', { message });
        throw new Error(message);
      }

      return data.vehicle as PlateVehicleData;
    },
    onError: (error: Error) => toast.error(error.message),
  });
}

/**
 * Multas de um veículo (leitura do banco) + sincronização sob demanda.
 */
export function useVehicleFines(vehicleId: string | null, enabled = true) {
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id ?? null;
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['vehicle-fines', tenantId, vehicleId],
    enabled: !!vehicleId && !!tenantId && enabled,
    staleTime: 30_000,
    queryFn: async (): Promise<{ items: VehicleFine[]; syncedAt: string | null; totalOpen: number }> => {
      const { data, error } = await (supabase.rpc as any)('list_vehicle_fines', {
        p_vehicle_id: vehicleId,
        p_tenant_hint: tenantId,
      });
      if (error) {
        logger.error('Erro ao listar multas', { error: error.message });
        throw error;
      }
      const payload = (data || {}) as { items?: VehicleFine[]; synced_at?: string; total_open?: number };
      return {
        items: payload.items ?? [],
        syncedAt: payload.synced_at ?? null,
        totalOpen: Number(payload.total_open ?? 0),
      };
    },
  });

  const sync = useMutation({
    mutationFn: async (input: { plate: string; force?: boolean }) => {
      const { data, error } = await supabase.functions.invoke('plate-proxy', {
        body: {
          action: 'fines',
          plate: normalizePlateInput(input.plate),
          vehicle_id: vehicleId,
          tenant_id: tenantId,
          force: input.force ?? true,
        },
      });
      if (error) {
        const message = await readFunctionError(error);
        throw new Error(message);
      }
      return data as { fines?: VehicleFine[]; count?: number; throttled?: boolean };
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['vehicle-fines', tenantId, vehicleId] });
      queryClient.invalidateQueries({ queryKey: ['tenant-vehicles'] });
      if (result?.throttled) {
        toast.info('Multas consultadas recentemente. Aguarde alguns minutos.');
      } else {
        toast.success(`${result?.count ?? 0} multa(s) sincronizada(s)`);
      }
    },
    onError: (error: Error) => toast.error(error.message),
  });

  return {
    fines: query.data?.items ?? [],
    syncedAt: query.data?.syncedAt ?? null,
    totalOpen: query.data?.totalOpen ?? 0,
    isLoading: query.isLoading,
    sync,
  };
}
